/**
 * Diagnostic overlay (?debug=1): active backend and latency of the JS segment
 * (pointerdown → trigger return). The real perceived latency is measured
 * with hardware; this compares backends under equal conditions (M1).
 */

import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { useAudio } from "@/providers/audio-provider";

const REFRESH_MS = 250;

export function LatencyOverlay() {
  const audio = useAudio();
  const [tick, setTick] = createSignal(0);

  onMount(() => {
    const id = setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    onCleanup(() => {
      clearInterval(id);
    });
  });

  // The LatencyMeter is not reactive: `tick` forces the periodic re-read.
  const dispatch = () => {
    tick();
    const last = audio.meter.last()?.dispatchMs.toFixed(2) ?? "—";
    return `dispatch: ${last} ms (n=${audio.meter.count()})`;
  };

  // avg hides tail jitter; p95/max is what is perceived as "laggy".
  const distribution = () => {
    tick();
    const avg = audio.meter.averageMs().toFixed(2);
    const p50 = audio.meter.percentileMs(50).toFixed(2);
    const p95 = audio.meter.percentileMs(95).toFixed(2);
    const max = audio.meter.maxMs().toFixed(2);
    return `avg: ${avg} p50: ${p50} p95: ${p95} max: ${max} ms`;
  };

  return (
    <Show when={audio.debugEnabled}>
      <div class="pointer-events-none fixed left-2 top-2 z-50 rounded-lg bg-black/70 px-3 py-2 font-mono text-xs text-green-400">
        <div>backend: {audio.backend()}</div>
        <div>{dispatch()}</div>
        <div>{distribution()}</div>
      </div>
    </Show>
  );
}
